import { Component, OnInit, inject, ChangeDetectorRef, HostListener, OnDestroy } from '@angular/core';
import { Button, ButtonDirective } from "primeng/button";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { FormBuilder } from "@components/form-builder/form-builder";
import { Field, FieldService } from '@6grain/masofa-monolith-api-client/crop-monitoring';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { IMsfFormBuilder } from '@components/form-builder/form-builder';
import {
  RegionService,
  SoilTypeService,
  AgroclimaticZoneService,
  FirmService,
  PersonService
} from '@6grain/masofa-monolith-api-client/dictionaries';
import { HttpClient } from '@angular/common/http';
import { RegionDictionaryService, RegionDictionaryNode } from '@services/region-dictionary.service';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import {OldMapComponent} from '@components/old-map/old-map.component';
import {SiblingPolygon} from '@components/old-map/managers';
import {MsfTreeSelect} from '@components/tree-select/tree-select';
import {RegionManagerService} from '@components/openstreetmap/managers/region-manager.service';
import {MsfInput} from '@components/ui/forms/input/input';
import {environment} from '@root/src/environments/environment';
import { EMPTY, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators';


@Component({
  selector: 'app-modal-form-add-field',
  standalone: true,
  templateUrl: './modal-form-add-field.html',
  imports: [
    Button,
    FormBuilder,
    ButtonDirective,
    TranslatePipe,
    ConfirmDialogModule,
    ToastModule,
    FormsModule,
    CommonModule,
    OldMapComponent,
    MsfTreeSelect,
    MsfInput,
  ],
  providers: [ConfirmationService, MessageService],
  styleUrl: './modal-form-add-field.scss'
})
export class ModalFormAddField implements OnInit, OnDestroy {
  formData: Field | any = {};
  fieldArea: number = 0;
  isSaving: boolean = false;
  latitude: number | null = null;
  longitude: number | null = null;
  mapCenter: [number, number] = [environment.map.lng, environment.map.lat];
  existingFields: any[] = [];
  existingFieldsPolygons: SiblingPolygon[] = [];
  validationError: string | null = null;
  isLoadingFields: boolean = false;
  private mapZoom: number = 13;
  private readonly coordinateChange$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  // Region tree
  regionTree: RegionDictionaryNode[] = [];
  filteredRegionTree: RegionDictionaryNode[] = [];
  showRegionDropdown = false;
  regionSearch = '';
  selectedRegionLabel = '';
  expandedRegionIds = new Set<string>();
  private regionIndex = new Map<string, RegionDictionaryNode>();
  preferredDecimalSeparator: '.' | ',' | null = null;
  decimalSeparatorMismatch: '.' | ',' | null = null;

  protected readonly translate = inject(TranslateService);
  private readonly messageService = inject(MessageService);
  private readonly http = inject(HttpClient);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly regionDictionaryService = inject(RegionDictionaryService);
  readonly regionManagerService = inject(RegionManagerService);

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private fieldService: FieldService,
    private regionService: RegionService,
    private soilTypeService: SoilTypeService,
    private agroClimaticService: AgroclimaticZoneService,
    private firmService: FirmService,
    private personService: PersonService
  ) {
    this.formData = {};
  }

  ngOnInit(): void {
    this.loadAgroProducers();
    this.loadRegions();
    // Initialize zoom from input (default is 13)
    this.mapZoom = 13;
    this.selectedRegionLabel = this.getAllRegionsLabel();

    // Subscribe to language changes
    this.translate.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateRegionNames();
        this.updateSelectedRegionLabel();
        this.cdr.detectChanges();
      });

    this.coordinateChange$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(() => {
          const lat = this.toCoordinateNumber(this.latitude);
          const lng = this.toCoordinateNumber(this.longitude);

          if (lat === null || lng === null) {
            return EMPTY;
          }

          return this.regionService.dictionariesRegionGetRegionByPointGet(lng, lat).pipe(
            catchError(() => EMPTY)
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (region) => {
          if (region?.id) {
            this.formData.regionId = region.id;
            this.updateSelectedRegionLabel();
            this.cdr.detectChanges();
          }
        }
      });
  }

  getFormConfig(): IMsfFormBuilder[] {
    return [
      {
        name: 'name',
        title: 'Name',
        type: 'input',
        placeholder: 'Enter field name',
        required: true
      },
      {
        name: 'bonitetScore',
        title: 'Bonitet score',
        type: 'input',
        placeholder: '1-100'
      },
      {
        name: 'soilTypeId',
        title: 'Soil type',
        type: 'select',
        selectService: this.soilTypeService,
        selectServiceMethod: "dictionariesSoilTypeGetByQueryPost",
        placeholder: 'Select soil type'
      },
      {
        name: 'agroclimaticZoneId',
        title: 'Agroclimatic zone',
        type: 'select',
        selectService: this.agroClimaticService,
        selectServiceMethod: "dictionariesAgroclimaticZoneGetByQueryPost",
        placeholder: 'Select agroclimatic zone'
      },
      {
        name: 'classifier',
        title: 'Classifier',
        type: 'input',
        placeholder: 'Classifier'
      },
      {
        name: 'comment',
        title: 'Comment',
        type: 'textarea',
        placeholder: 'Comment',
        options: {
          col: 'col-12'
        }
      }
    ];
  }

  private agroProducers: any[] = [];

  private loadAgroProducers(): void {
    this.personService.dictionariesPersonGetByQueryPost({}).subscribe({
      next: (persons) => {
        this.agroProducers = [...(persons || [])];
      },
      error: (err: any) => console.error('Error loading persons:', err)
    });

    this.firmService.dictionariesFirmGetByQueryPost({}).subscribe({
      next: (firms) => {
        this.agroProducers.push(...(firms || []));
      },
      error: (err: any) => console.error('Error loading firms:', err)
    });
  }


  onMapMove(event: any): void {
    if (!event) return;

    // Update zoom from event
    if (event.zoom !== undefined && event.zoom !== null) {
      this.mapZoom = event.zoom;
    }

    // Load fields if zoom >= 12 and we have bounds
    if (this.mapZoom >= 12 && event.bounds) {
      this.loadFieldsInViewByBounds(event.bounds);
    } else if (this.mapZoom < 12) {
      // Clear fields if zoom < 12
      this.existingFields = [];
      this.existingFieldsPolygons = [];
    }
  }

  onGeometryChange(geometry: any): void {
    // Save polygon to formData
    if (geometry) {
      // Ensure geometry is a string (WKT format)
      const polygonString = typeof geometry === 'string' ? geometry : geometry?.polygonJson || geometry?.geometry || geometry;

      this.updateCoordinatesFromPolygon(polygonString);

      // Automatically determine region by polygon center
      this.determineRegionByPolygon(polygonString);

      // Load existing fields when polygon is drawn
      if (this.mapZoom >= 12) {
        this.loadFieldsForPolygon(polygonString);
      }
    }
  }

  private loadFieldsForPolygon(polygonWkt: string): void {
    try {
      // Parse WKT to get bounds
      const coordsMatch = polygonWkt.match(/POLYGON\s*\(\(([^)]+)\)\)/i);
      if (!coordsMatch) return;

      const coordPairs = coordsMatch[1].trim().split(',').map(pair => {
        const [lon, lat] = pair.trim().split(/\s+/).map(Number);
        return { lon, lat };
      });

      if (coordPairs.length < 3) return;

      const lons = coordPairs.map(c => c.lon);
      const lats = coordPairs.map(c => c.lat);

      const bounds = {
        south: Math.min(...lats),
        north: Math.max(...lats),
        west: Math.min(...lons),
        east: Math.max(...lons)
      };

      // Expand bounds slightly to get surrounding fields
      const expandFactor = 0.01;
      bounds.south -= expandFactor;
      bounds.north += expandFactor;
      bounds.west -= expandFactor;
      bounds.east += expandFactor;

      this.loadFieldsInViewByBounds(bounds);
    } catch (error) {
      console.error('Error loading fields for polygon:', error);
    }
  }


  private loadFieldsInViewByBounds(bounds: { south: number; north: number; west: number; east: number }): void {
    // Only load fields if zoom >= 12
    if (this.mapZoom < 12) {
      this.existingFields = [];
      this.existingFieldsPolygons = [];
      return;
    }

    // Prevent multiple simultaneous requests
    if (this.isLoadingFields) {
      return;
    }

    this.isLoadingFields = true;

    const payload: any = {
      west: bounds.west,
      east: bounds.east,
      south: bounds.south,
      north: bounds.north,
      query: {}
    };

    // Use FieldService to get fields by coordinates (same as in gis-fields)
    const fieldServiceAny = this.fieldService as any;

    if (fieldServiceAny.cropMonitoringFieldGetFieldsByCoordinatesPost) {
      fieldServiceAny.cropMonitoringFieldGetFieldsByCoordinatesPost(payload).subscribe({
        next: (fields: any[]) => {
          this.existingFields = fields || [];
          this.existingFieldsPolygons = this.existingFields
            .filter(f => f.polygonJson)
            .map(f => ({
              polygon: f.polygonJson,
              label:f?.name,
              id:f?.id,
              color: 'rgba(255, 0, 0, 0.3)' // Red color for existing fields
            }));
          this.isLoadingFields = false;
        },
        error: (err: any) => {
          console.error('Error loading fields:', err);
          this.existingFields = [];
          this.existingFieldsPolygons = [];
          this.isLoadingFields = false;
        }
      });
    } else {
      // Fallback: use HttpClient directly
      const token = localStorage.getItem('access_token');
      if (!token) {
        console.warn('No access token found');
        return;
      }

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const apiUrl = `/cropMonitoring/Field/GetFieldsByCoordinates`;
      this.http.post<any[]>(apiUrl, payload, { headers }).subscribe({
        next: (fields) => {
          this.existingFields = fields || [];
          this.existingFieldsPolygons = this.existingFields
            .filter(f => f.polygonJson)
            .map(f => ({
              polygon: f.polygonJson,
              label:f?.name,
              id:f?.id,
              color: 'rgba(255, 0, 0, 0.3)'
            }));
          this.isLoadingFields = false;
        },
        error: (err: any) => {
          console.error('Error loading fields:', err);
          this.existingFields = [];
          this.existingFieldsPolygons = [];
          this.isLoadingFields = false;
        }
      });
    }
  }

  onPolygonChange(polygonJson: any): void {
    // Handle polygon change - can be string or object
    const polygonString = typeof polygonJson === 'string' ? polygonJson : polygonJson?.polygonJson || polygonJson?.geometry;

    if (polygonString) {
      this.formData.polygonJson = polygonString;
      this.formData.polygon = polygonString; // Also save to polygon for two-way binding
      // Don't clear validationError here - let it be cleared by map component

      this.updateCoordinatesFromPolygon(polygonString);

      // Automatically determine region by polygon center
      this.determineRegionByPolygon(polygonString);

      // Load existing fields when polygon is drawn
      if (this.mapZoom >= 12) {
        this.loadFieldsForPolygon(polygonString);
      }
    }
  }

  onValidationError(error: string | null | any): void {
    if (error) {
      // Translate error key if it's a translation key, otherwise use as-is
      const translatedError = error.startsWith('FIELD_DRAWING.ERROR.')
        ? this.translate.instant(error) || error
        : error;
      this.validationError = translatedError;
    } else {
      this.validationError = null;
    }
    this.cdr.detectChanges();
  }

  onCoordinatesChange(): void {
    const lat = this.toCoordinateNumber(this.latitude);
    const lng = this.toCoordinateNumber(this.longitude);

    this.latitude = lat;
    this.longitude = lng;

    if (lat === null || lng === null) {
      return;
    }

    this.mapCenter = [lng, lat];
    this.coordinateChange$.next(`${lat}:${lng}`);
  }

  private determineRegionByPolygon(polygonWkt: string): void {
    try {
      const coordPairs = this.parsePolygonCoordinates(polygonWkt);
      if (coordPairs.length < 3) return;

      const {lng: centerLon, lat: centerLat} = this.calculatePolygonCenter(coordPairs);

      // Use alternative method to find region from nearby fields
      this.tryDetermineRegionFromFields(centerLon, centerLat);
    } catch (error) {
      console.error('Error determining region:', error);
    }
  }

  private updateCoordinatesFromPolygon(polygonWkt: string): void {
    const coordPairs = this.parsePolygonCoordinates(polygonWkt);
    if (coordPairs.length < 3) return;

    const {lat, lng} = this.calculatePolygonCenter(coordPairs);
    this.latitude = lat;
    this.longitude = lng;
    this.mapCenter = [lng, lat];
  }

  private parsePolygonCoordinates(polygonWkt: string): Array<{ lng: number; lat: number }> {
    const coordsMatch = polygonWkt.match(/POLYGON\s*\(\(([^)]+)\)\)/i);

    if (!coordsMatch) return [];

    const coordinates = coordsMatch[1]
      .trim()
      .split(',')
      .map(pair => {
        const [lng, lat] = pair.trim().split(/\s+/).map(Number);
        return { lng, lat };
      })
      .filter(({lng, lat}) => Number.isFinite(lng) && Number.isFinite(lat));

    if (coordinates.length > 1) {
      const first = coordinates[0];
      const last = coordinates[coordinates.length - 1];
      if (first.lng === last.lng && first.lat === last.lat) {
        coordinates.pop();
      }
    }

    return coordinates;
  }

  private calculatePolygonCenter(coordPairs: Array<{ lng: number; lat: number }>): { lng: number; lat: number } {
    const lng = coordPairs.reduce((sum, coord) => sum + coord.lng, 0) / coordPairs.length;
    const lat = coordPairs.reduce((sum, coord) => sum + coord.lat, 0) / coordPairs.length;

    return {
      lng: Number(lng.toFixed(10)),
      lat: Number(lat.toFixed(10))
    };
  }

  private toCoordinateNumber(value: number | string | null | undefined): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const parsed = typeof value === 'number' ? value : Number(String(value).replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : null;
  }

  private tryDetermineRegionFromFields(centerLon: number, centerLat: number): void {
    // Alternative: load fields in small area around center and get region from first field
    const smallBounds = {
      west: centerLon - 0.01,
      east: centerLon + 0.01,
      south: centerLat - 0.01,
      north: centerLat + 0.01
    };

    const payload: any = {
      west: smallBounds.west,
      east: smallBounds.east,
      south: smallBounds.south,
      north: smallBounds.north,
      query: {}
    };

    const fieldServiceAny = this.fieldService as any;
    if (fieldServiceAny.cropMonitoringFieldGetFieldsByCoordinatesPost) {
      fieldServiceAny.cropMonitoringFieldGetFieldsByCoordinatesPost(payload).subscribe({
        next: (fields: any[]) => {
          try {
            if (fields && fields.length > 0) {
              const firstField = fields[0];
              if (firstField?.regionId) {
                this.formData.regionId = firstField.regionId;
                this.updateSelectedRegionLabel();
                this.cdr.detectChanges();
              } else if (firstField?.region?.id) {
                this.formData.regionId = firstField.region.id;
                this.updateSelectedRegionLabel();
                this.cdr.detectChanges();
              }
            }
          } catch (error) {
            // Silently fail - region will remain undefined, user can select manually
          }
        },
        error: (err: any) => {
          // Silently fail - region will remain undefined, user can select manually
          // This is normal if there are no fields in the area
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.coordinateChange$.complete();
  }

  save(): void {
    if (this.validationError) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('Validation Error'),
        detail: this.validationError
      });
      return;
    }

    if (!this.formData.name) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('Validation Error'),
        detail: this.translate.instant('Field name is required')
      });
      return;
    }

    // Check if polygon is set (either polygonJson or polygon)
    const polygonValue = this.formData.polygonJson || this.formData.polygon;
    if (!polygonValue) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('Validation Error'),
        detail: this.translate.instant('Field polygon is required')
      });
      return;
    }

    const transformedFormData = {
      ...this.formData,
      fieldArea: this.fieldArea || null,
      polygonJson: polygonValue,
      status: 1 // Set status to Active (1) for new fields
    };

    // Remove polygon property if it exists (we only need polygonJson)
    delete transformedFormData.polygon;

    this.isSaving = true;

    this.fieldService.cropMonitoringFieldCreatePost(transformedFormData).subscribe({
      next: (response) => {
        this.isSaving = false;
        this.ref.close({ success: true, data: response });
      },
      error: (error: any) => {
        this.isSaving = false;
        console.error('Error while saving:', error);

        // Try to get localized error message
        let errorMessage = this.translate.instant('Error creating field');

        if (error?.error) {
          // Check for different error formats
          if (typeof error.error === 'string') {
            errorMessage = error.error;
          } else if (error.error?.message) {
            errorMessage = error.error.message;
          } else if (error.error?.error?.message) {
            errorMessage = error.error.error.message;
          } else if (error.error?.title) {
            errorMessage = error.error.title;
          }

          // Try to translate common error messages
          const errorKey = errorMessage.toLowerCase();
          if (errorKey.includes('intersect') || errorKey.includes('пересекает')) {
            errorMessage = this.translate.instant('Field polygon intersects with existing fields') ||
                          this.translate.instant('The field polygon intersects with existing fields') ||
                          errorMessage;
          } else if (errorKey.includes('contain') || errorKey.includes('содержит')) {
            errorMessage = this.translate.instant('Field polygon is contained within existing field') ||
                          this.translate.instant('The field polygon is contained within an existing field') ||
                          errorMessage;
          } else if (errorKey.includes('required') || errorKey.includes('обязательно')) {
            errorMessage = this.translate.instant('Required fields are missing') || errorMessage;
          }
        } else if (error?.message) {
          errorMessage = error.message;
        }

        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('Error') || 'Error',
          detail: errorMessage
        });
      }
    });
  }

  cancel(): void {
    this.ref.close();
  }

  // Region tree methods
  private loadRegions(): void {
    this.regionDictionaryService.getRegionTree().subscribe({
      next: (regionTree) => {
        const regionNodes = regionTree.map(region => ({
          ...region,
          expanded: false,
          children: region.children
            ? region.children.map(child => ({
                ...child,
                expanded: false,
                children: child.children ?? []
              }))
            : []
        }));

        const sortedRegionNodes = this.sortRegionsByLocalizedName(regionNodes);

        const allOption: RegionDictionaryNode = {
          id: 'all',
          name: this.getAllRegionsLabel(),
          parentId: null,
          level: 0,
          regionMapId: null,
          expanded: false,
          children: []
        };

        this.regionTree = [allOption, ...sortedRegionNodes];
        this.buildRegionIndex(this.regionTree);
        this.selectedRegionLabel = this.getAllRegionsLabel();
        this.filteredRegionTree = this.buildFilteredRegionTree('');
        this.cdr.detectChanges();
      },
      error: (err: any) => {
        console.error('Error loading regions:', err);
        this.regionTree = [];
        this.filteredRegionTree = [];
        this.cdr.detectChanges();
      }
    });
  }

  private sortRegionsByLocalizedName(regions: RegionDictionaryNode[]): RegionDictionaryNode[] {
    return regions
      .map(region => ({
        ...region,
        children: region.children
          ? this.sortRegionsByLocalizedName(region.children)
          : []
      }))
      .sort((a, b) => {
        const nameA = this.getLocalizedRegionName(a).toLowerCase();
        const nameB = this.getLocalizedRegionName(b).toLowerCase();
        return nameA.localeCompare(nameB, this.translate.currentLang || 'ru');
      });
  }

  getLocalizedRegionName(node: RegionDictionaryNode | null | undefined): string {
    if (!node) {
      return '';
    }
    const currentLang = this.translate.currentLang || 'ru-RU';
    const names = node.names || {};
    return (
      names[currentLang] ??
      names[currentLang.split('-')[0]] ??
      node.name ??
      ''
    );
  }

  getAllRegionsLabel(): string {
    return this.translate.instant('Region') || 'All regions';
  }

  private buildRegionIndex(nodes: RegionDictionaryNode[]): void {
    this.regionIndex.clear();
    const flattened = this.flattenRegionTree(nodes);
    flattened.forEach(node => this.regionIndex.set(node.id, node));
  }

  private flattenRegionTree(nodes: RegionDictionaryNode[]): RegionDictionaryNode[] {
    const stack = [...nodes];
    const result: RegionDictionaryNode[] = [];

    while (stack.length) {
      const node = stack.pop();
      if (!node || node.id === 'all') {
        continue;
      }
      result.push(node);
      if (node.children?.length) {
        stack.push(...node.children);
      }
    }

    return result;
  }

  private buildFilteredRegionTree(searchTerm: string): RegionDictionaryNode[] {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      return this.regionTree.map(region => ({
        ...region,
        name: region.id === 'all' ? this.getAllRegionsLabel() : this.getLocalizedRegionName(region),
        children: region.children ? region.children.map(child => ({
          ...child,
          name: this.getLocalizedRegionName(child)
        })) : []
      }));
    }

    const filtered: RegionDictionaryNode[] = [];

    this.regionTree.forEach(region => {
      const localizedRegionName = region.id === 'all' ? this.getAllRegionsLabel() : this.getLocalizedRegionName(region);

      if (region.id === 'all') {
        if (localizedRegionName.toLowerCase().includes(term)) {
          filtered.push({ ...region, name: localizedRegionName, children: [] });
        }
        return;
      }

      const matchesRegion = localizedRegionName.toLowerCase().includes(term);
      const matchingChildren =
        region.children
          ?.filter(child => {
            const localizedChildName = this.getLocalizedRegionName(child);
            return localizedChildName.toLowerCase().includes(term);
          })
          .map(child => ({
            ...child,
            name: this.getLocalizedRegionName(child)
          })) ?? [];

      if (matchesRegion || matchingChildren.length) {
        filtered.push({
          ...region,
          name: localizedRegionName,
          expanded: matchesRegion || matchingChildren.length > 0,
          children: matchesRegion
            ? (region.children ? region.children.map(child => ({
                ...child,
                name: this.getLocalizedRegionName(child)
              })) : [])
            : matchingChildren
        });
      }
    });

    return filtered;
  }

  toggleRegionDropdown(): void {
    this.showRegionDropdown = !this.showRegionDropdown;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.region-selector')) {
      this.showRegionDropdown = false;
    }
  }

  onRegionSearchChange(): void {
    this.filteredRegionTree = this.buildFilteredRegionTree(this.regionSearch);
  }

  toggleRegionExpansion(region: RegionDictionaryNode, event?: Event): void {
    event?.stopPropagation();
    if (this.expandedRegionIds.has(region.id)) {
      this.expandedRegionIds.delete(region.id);
      region.expanded = false;
    } else {
      this.expandedRegionIds.add(region.id);
      region.expanded = true;
    }
  }

  selectRegion(region: RegionDictionaryNode, closeDropdown: boolean = true): void {
    if (region.id === 'all') {
      this.selectedRegionLabel = this.getAllRegionsLabel();
      this.formData.regionId = null;
      if (closeDropdown) {
        this.showRegionDropdown = false;
      }
      return;
    }

    this.selectedRegionLabel = this.getLocalizedRegionName(region);
    this.formData.regionId = region.id;
    if (closeDropdown) {
      this.showRegionDropdown = false;
    }
  }

  selectDistrict(region: RegionDictionaryNode, district: RegionDictionaryNode, event?: Event): void {
    event?.stopPropagation();
    this.selectedRegionLabel = this.getLocalizedRegionName(district);
    this.formData.regionId = district.id;
    this.showRegionDropdown = false;
  }

  private updateRegionNames(): void {
    if (this.regionTree.length > 0) {
      const allOption = this.regionTree[0];
      if (allOption.id === 'all') {
        allOption.name = this.getAllRegionsLabel();
      }

      this.filteredRegionTree = this.buildFilteredRegionTree(this.regionSearch);
    }
  }

  private updateSelectedRegionLabel(): void {
    if (!this.formData.regionId) {
      this.selectedRegionLabel = this.getAllRegionsLabel();
      return;
    }

    const node = this.regionIndex.get(String(this.formData.regionId));
    if (node) {
      this.selectedRegionLabel = this.getLocalizedRegionName(node);
    }
  }

  onDecimalSeparatorUsed(separator: '.' | ','): void {
    if (!this.preferredDecimalSeparator) {
      this.preferredDecimalSeparator = separator;
    }
  }

  getDecimalSeparatorMismatchMessage(): string {
    if (!this.decimalSeparatorMismatch) {
      return '';
    }
    return this.translate.instant('DECIMAL_SEPARATOR_MISMATCH', { separator: this.decimalSeparatorMismatch });
  }
}
